import { BaseDragger } from "./BaseDragger";
import { DraggerType, type Point2D, type Point3D } from "./types";
import { selectionService } from "../selection";
import { deleteObj } from "../core/delete";
import { Mode, SubentSelectionMode, OpenMode } from "../core/constants";
import { openAs, getEntityHandle, findEntityByHandle } from "../core/oda";
import {
  DeleteEntityCommand,
  type DeleteEntityParams,
} from "../commands/DeleteEntityCommand";
import type { OverlayStyle } from "../overlay/types";
import { Logger } from "@/logger";
import type { DrawingModule } from "../types/DrawingWeb";
import { eraserConfig } from "./eraserConfig";
import { UNDO_LAYER_NAME } from "../database";

const ERASER_MIN_STEP_PX = 5;

/**
 * EraserDragger - Delete entities by dragging over them.
 *
 * Performs a crossing selection at each drag point using the same GPU
 * selection API as SelectDragger. Entities on frozen or locked layers are
 * excluded. All collected entities are deleted as a single undoable command
 * when the pointer is released.
 *
 * Visual feedback: a growing polyline trail shows the eraser path in real
 * time. The trail is cleared on pointer release.
 */
export class EraserDragger extends BaseDragger {
  readonly type = DraggerType.Eraser;

  private collectedHandles = new Set<string>();
  private trailOverlayId: string | null = null;
  private lastCheckScreen: Point2D | null = null;
  private layerStatusCache = new Map<
    string,
    { frozen: boolean; locked: boolean; isInUndoLayer: boolean }
  >();

  // ============== Dragger Callbacks ==============

  protected onStart(screen: Point2D, world: Point3D): void {
    this.layerStatusCache.clear();
    this.lastCheckScreen = screen;
    // Add the start point twice so the renderer draws a dot even on a tap
    // (renderPolyline requires at least 2 points; zero-length segment with
    // lineCap:"round" renders as a filled circle).
    this.startTrail(screen);
    this.collectEntitiesAt(world);
  }

  protected onDrag(screen: Point2D, world: Point3D): void {
    // Always extend the trail (no throttle — keeps the visual smooth)
    this.extendTrail(screen);

    // Throttle the GPU selection check
    if (this.lastCheckScreen) {
      const dx = screen.x - this.lastCheckScreen.x;
      const dy = screen.y - this.lastCheckScreen.y;
      if (Math.sqrt(dx * dx + dy * dy) < ERASER_MIN_STEP_PX) return;
    }

    this.lastCheckScreen = screen;
    this.collectEntitiesAt(world);
  }

  protected onEnd(_screen: Point2D, world: Point3D): void {
    this.collectEntitiesAt(world);
    this.clearTrail();
    this.executeDelete();
    this.resetState();
    this.viewer.update();
  }

  protected onCancel(): void {
    this.clearTrail();
    this.resetState();
    super.onCancel();
  }

  // ============== Selection ==============

  private collectEntitiesAt(world: Point3D): void {
    const db = this.viewer.database;
    if (!db) return;

    const vpId = db.activeViewportId();
    if (!vpId || vpId.isNull()) return;

    const aperture = this.viewer.screenDistanceToWorld(eraserConfig.aperturePx);

    const pointArray = new this.module.OdGePoint3dArray();
    const pt1 = new this.module.OdGePoint3d(
      world.x - aperture,
      world.y - aperture,
      world.z,
    );
    const pt2 = new this.module.OdGePoint3d(
      world.x + aperture,
      world.y + aperture,
      world.z,
    );
    pointArray.push_back(pt1);
    pointArray.push_back(pt2);
    deleteObj(pt1);
    deleteObj(pt2);

    let selection: DrawingModule.OdDbSelectionSet | null = null;
    try {
      selection = this.module.OdDbSelectionSet.select1(
        vpId,
        2,
        pointArray.asArrayPtr(),
        Mode.kCrossing,
        SubentSelectionMode.kDisableSubents,
        null as any,
      );
    } catch (error) {
      Logger.captureException(error, {
        context: "EraserDragger.collectEntitiesAt",
        world: { x: world.x, y: world.y, z: world.z },
        aperture,
        vpId: vpId?.toString(),
      });
      deleteObj(pointArray);
      return;
    }

    deleteObj(pointArray);

    if (!selection) return;

    const idArray = selection.objectIdArray();
    for (let i = 0; i < idArray.size(); i++) {
      const entityId = idArray.get(i);
      if (!entityId || entityId.isNull() || entityId.isErased()) continue;

      if (this.isEntityDeletable(entityId)) {
        const handle = getEntityHandle(entityId);
        if (handle) {
          this.collectedHandles.add(handle);
        }
      }
    }

    deleteObj(selection);
  }

  private isEntityDeletable(entityId: DrawingModule.OdDbObjectId): boolean {
    const db = this.viewer.database;
    if (!db) return false;

    let layerName: string;
    try {
      const entity = openAs(
        entityId,
        this.module.OdDbEntity,
        OpenMode.kForRead,
      );
      const layerOdStr = entity.layer();
      layerName = layerOdStr.toString();
      deleteObj(layerOdStr);
      deleteObj(entity);
    } catch {
      return false;
    }

    const status = this.getLayerStatus(layerName, db);
    return !status.frozen && !status.locked && !status.isInUndoLayer;
  }

  private getLayerStatus(
    layerName: string,
    db: DrawingModule.OdDbDatabase,
  ): { frozen: boolean; locked: boolean; isInUndoLayer: boolean } {
    const cached = this.layerStatusCache.get(layerName);
    if (cached) return cached;

    const layerTableId = db.getLayerTableId();
    const layerTable = openAs(
      layerTableId,
      this.module.OdDbLayerTable,
      OpenMode.kForRead,
    );

    const layerNameStr = new this.module.OdString();
    layerNameStr.set(layerName);

    if (!layerTable.has1(layerNameStr)) {
      deleteObj(layerNameStr);
      deleteObj(layerTable);
      const status = { frozen: false, locked: false, isInUndoLayer: false };
      this.layerStatusCache.set(layerName, status);
      return status;
    }

    const layerId = layerTable.getAt3(layerNameStr, false);
    deleteObj(layerNameStr);
    deleteObj(layerTable);

    const layerRecord = openAs(
      layerId,
      this.module.OdDbLayerTableRecord,
      OpenMode.kForRead,
    );
    const frozen = layerRecord.isFrozen();
    const locked = layerRecord.isLocked();
    const isInUndoLayer = layerName === UNDO_LAYER_NAME;
    deleteObj(layerRecord);

    const status = { frozen, locked, isInUndoLayer };
    this.layerStatusCache.set(layerName, status);
    return status;
  }

  // ============== Deletion ==============

  private executeDelete(): void {
    const db = this.viewer.database;
    if (!db || this.collectedHandles.size === 0) return;

    const ctx = selectionService.getSelectionSet().getContext();
    if (!ctx) return;

    const deleteParams: DeleteEntityParams[] = [];
    const resolvedIds: DrawingModule.OdDbObjectId[] = [];

    for (const handle of this.collectedHandles) {
      const entityId = findEntityByHandle(this.module, db, handle);
      if (!entityId) continue;

      let layerName: string;
      try {
        const entity = openAs(
          entityId,
          this.module.OdDbEntity,
          OpenMode.kForRead,
        );
        const layerOdStr = entity.layer();
        layerName = layerOdStr.toString();
        deleteObj(layerOdStr);
        deleteObj(entity);
      } catch {
        deleteObj(entityId);
        continue;
      }

      deleteParams.push({ entityId, originalLayerName: layerName });
      resolvedIds.push(entityId);
    }

    if (deleteParams.length > 0) {
      const command = new DeleteEntityCommand(this.viewer, deleteParams);
      ctx.history.execute(command);
    }

    for (const entityId of resolvedIds) {
      deleteObj(entityId);
    }
  }

  // ============== Trail Overlay ==============

  private startTrail(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    // Trail width matches the selection diameter so the visual exactly
    // represents the erased area. Two identical start points ensure the
    // renderer (requires length >= 2) draws a dot on a simple tap.
    const trailStyle: OverlayStyle = {
      strokeColor: "rgba(255, 68, 68, 0.5)",
      strokeWidth: eraserConfig.aperturePx * 2,
      opacity: 1,
    };
    this.trailOverlayId = overlay.createPolyline(
      [
        { x: screen.x, y: screen.y },
        { x: screen.x, y: screen.y },
      ],
      trailStyle,
    );
  }

  private extendTrail(screen: Point2D): void {
    if (!this.trailOverlayId) return;
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    overlay.addPointToPolyline(this.trailOverlayId, {
      x: screen.x,
      y: screen.y,
    });
  }

  private clearTrail(): void {
    if (this.trailOverlayId && this.viewer.overlayManager) {
      this.viewer.overlayManager.remove(this.trailOverlayId);
      this.trailOverlayId = null;
    }
  }

  // ============== State ==============

  private resetState(): void {
    this.collectedHandles.clear();
    this.lastCheckScreen = null;
    this.layerStatusCache.clear();
  }

  // ============== Lifecycle ==============

  protected onDeactivate(): void {
    this.clearTrail();
    this.resetState();
  }

  dispose(): void {
    this.clearTrail();
    super.dispose();
  }
}
